import { calculateMonthlyIncomeTax } from './tax';
import { calculateNationalInsurance } from './nationalInsurance';

export const QUALIFIED_EARNINGS = {
  LOWER_LIMIT: 6240,
  UPPER_LIMIT: 50270
};

export function calculatePension(
  grossAnnualSalary,
  employeePercentage,
  employerPercentage,
  isSalarySacrifice = false,
  taxCode = '1257L'
) {
  const grossMonthlySalary = grossAnnualSalary / 12;
  
  if (isSalarySacrifice) {
    // Salary sacrifice: contributions on full salary, deducted before tax/NI
    const employeeContribution = (grossAnnualSalary * employeePercentage) / 100 / 12;
    const employerContribution = (grossAnnualSalary * employerPercentage) / 100 / 12;
    
    // Calculate tax and NI on reduced salary
    const incomeTax = calculateMonthlyIncomeTax(grossAnnualSalary, employeeContribution * 12, taxCode);
    const niCalculation = calculateNationalInsurance(grossMonthlySalary, employeeContribution);
    
    const salaryAfterPension = grossMonthlySalary - employeeContribution;
    const takeHomePay = salaryAfterPension - incomeTax - niCalculation.employeeNI;
    const totalWealth = takeHomePay + employeeContribution + employerContribution;
    
    return {
      grossMonthlySalary,
      employeeContribution,
      employerContribution,
      salaryAfterPension,
      incomeTax,
      employeeNI: niCalculation.employeeNI,
      employerNI: niCalculation.employerNI,
      takeHomePay,
      totalWealth,
    };
  } else {
    // Traditional pension: calculate tax/NI first, then deduct employee contribution from net pay
    const incomeTax = calculateMonthlyIncomeTax(grossAnnualSalary, 0, taxCode);
    const niCalculation = calculateNationalInsurance(grossMonthlySalary, 0);
    
    // Calculate qualified earnings for employer contribution
    const qualifiedEarnings = Math.min(
      Math.max(grossAnnualSalary - QUALIFIED_EARNINGS.LOWER_LIMIT, 0), 
      QUALIFIED_EARNINGS.UPPER_LIMIT - QUALIFIED_EARNINGS.LOWER_LIMIT
    );
    const employerContribution = (qualifiedEarnings * employerPercentage) / 100 / 12;
    
    // Calculate gross pension contribution (what goes into pension pot)
    const grossEmployeeContribution = (qualifiedEarnings * employeePercentage) / 100 / 12;
    
    // Calculate net contribution (what employee actually pays from take-home)
    // Assuming basic rate tax relief (20%) - this is what appears on payslip
    const netEmployeeContribution = grossEmployeeContribution * 0.8;
    
    // Tax relief will be calculated based on actual affordable contribution
    
    const netPayBeforeEmployeePension = grossMonthlySalary - incomeTax - niCalculation.employeeNI;
    
    // Check affordability - employee cannot pay more than their net pay
    const affordableNetContribution = Math.min(netEmployeeContribution, netPayBeforeEmployeePension);
    const actualGrossContribution = affordableNetContribution / 0.8; // Gross amount based on what they can afford
    const actualTaxRelief = actualGrossContribution * 0.2;
    
    const salaryAfterPension = grossMonthlySalary; // No change for traditional pension
    const takeHomePay = netPayBeforeEmployeePension - affordableNetContribution;
    const totalWealth = takeHomePay + actualGrossContribution + employerContribution;
    
    return {
      grossMonthlySalary,
      employeeContribution: affordableNetContribution, // What employee pays from net pay
      grossEmployeeContribution: actualGrossContribution, // What actually goes into pension
      taxRelief: actualTaxRelief, // What HMRC adds
      employerContribution,
      salaryAfterPension,
      incomeTax,
      employeeNI: niCalculation.employeeNI,
      employerNI: niCalculation.employerNI,
      takeHomePay,
      totalWealth,
    };
  }
}

export function comparePensions(
  grossAnnualSalary,
  currentEmployeePercentage,
  currentEmployerPercentage,
  sacrificeEmployeePercentage,
  sacrificeEmployerPercentage,
  taxCode = '1257L'
) {
  const current = calculatePension(grossAnnualSalary, currentEmployeePercentage, currentEmployerPercentage, false, taxCode);
  const salarySacrifice = calculatePension(grossAnnualSalary, sacrificeEmployeePercentage, sacrificeEmployerPercentage, true, taxCode);
  
  return {
    current,
    salarySacrifice,
    takeHomeDifference: salarySacrifice.takeHomePay - current.takeHomePay,
    wealthDifference: salarySacrifice.totalWealth - current.totalWealth,
  };
}